import React, { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface GridDotsProps {
  containerRef?: React.RefObject<HTMLElement>;
}

const GridDots: React.FC<GridDotsProps> = ({ containerRef }) => {
  const { theme } = useTheme();
  const dotRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const dotColor = theme === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)';
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const el = dotRef.current;
      const rect = containerRef?.current?.getBoundingClientRect();
      if (el && rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const mask = `radial-gradient(circle 180px at ${x}px ${y}px, black 0%, black 40%, rgba(0,0,0,0.5) 70%, transparent 100%)`;
        el.style.maskImage = mask;
        el.style.webkitMaskImage = mask;
      }
      rafRef.current = null;
    });
  }, [containerRef]);

  const handleMouseEnter = useCallback(() => {
    const el = dotRef.current;
    if (el) el.style.opacity = '1';
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = dotRef.current;
    if (el) {
      el.style.opacity = '0';
      el.style.maskImage = '';
      el.style.webkitMaskImage = '';
    }
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const el = containerRef?.current;
    if (!el) return;

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef, isMobile, handleMouseMove, handleMouseEnter, handleMouseLeave]);

  // On mobile, don't render the grid at all
  if (isMobile) return null;

  return (
    <div
      ref={dotRef}
      className="absolute inset-0 pointer-events-none z-0"
      style={{
        opacity: 0,
        willChange: 'mask-image',
        transition: 'opacity 0.6s ease-in-out, mask-image 0.15s ease, -webkit-mask-image 0.15s ease',
        backgroundImage: `radial-gradient(${dotColor} 1.5px, transparent 1.5px)`,
        backgroundSize: '20px 20px',
      }}
    />
  );
};

export default GridDots;
